/**
 * Composable de transcription live (Google STT via backend WebSocket).
 *
 * - Se connecte à ws/pexitalk/transcription/
 * - Capture l'audio distant via Web Audio API (ScriptProcessorNode)
 * - Downsample 48 kHz → 16 kHz + conversion Float32 → Int16 LINEAR16
 * - Envoie les chunks base64 au backend toutes les ~100 ms
 * - Reçoit les résultats et empile les segments dans `lines`
 *
 * Activation conditionnelle : le backend refuse si le numéro n'appartient
 * pas à une campagne avec `transcription_enabled = true`.
 */
import { ref, readonly } from 'vue'
import type { SipController } from '../controllers/sip_controller'

export type TranscriptionStatus =
  | 'idle'        // inactif
  | 'connecting'  // WS en cours
  | 'checking'    // vérification campagne en cours
  | 'active'      // transcription en cours
  | 'denied'      // campagne sans transcription
  | 'error'       // erreur technique

export interface TranscriptionLine {
  text: string
  isFinal: boolean
}

const OUTPUT_SAMPLE_RATE = 16000
const PROCESSOR_BUFFER = 4096

function buildWsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${location.host}/ws/pexitalk/transcription/`
}

function downsampleToInt16(inputData: Float32Array, inputRate: number): Int16Array {
  const ratio = inputRate / OUTPUT_SAMPLE_RATE
  const outputLen = Math.floor(inputData.length / ratio)
  const output = new Int16Array(outputLen)
  for (let i = 0; i < outputLen; i++) {
    const src = Math.min(inputRate - 1, Math.floor(i * ratio))
    output[i] = Math.max(-32768, Math.min(32767, inputData[src] * 32768))
  }
  return output
}

function int16ToBase64(buf: Int16Array): string {
  const bytes = new Uint8Array(buf.buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

export function useTranscription() {
  const status = ref<TranscriptionStatus>('idle')
  const deniedReason = ref('')
  const lines = ref<TranscriptionLine[]>([])

  let ws: WebSocket | null = null
  let audioCtx: AudioContext | null = null
  let processor: ScriptProcessorNode | null = null
  let source: MediaStreamAudioSourceNode | null = null

  // ── API publique ─────────────────────────────────────────────────────────

  async function start(phone: string, sipController: SipController, language = 'fr-FR') {
    if (status.value === 'active' || status.value === 'connecting') return

    const stream = sipController.getRemoteStream()
    if (!stream) {
      status.value = 'error'
      return
    }

    lines.value = []
    deniedReason.value = ''
    status.value = 'connecting'

    ws = new WebSocket(buildWsUrl())

    ws.onopen = () => {
      status.value = 'checking'
      ws!.send(JSON.stringify({ type: 'transcription.start', phone, language }))
    }

    ws.onmessage = (ev) => {
      let msg: any
      try { msg = JSON.parse(ev.data) } catch { return }

      if (msg.type === 'transcription.ready') {
        status.value = 'active'
        _startAudio(stream)
      } else if (msg.type === 'transcription.denied') {
        status.value = 'denied'
        deniedReason.value = msg.reason ?? ''
        ws?.close()
      } else if (msg.type === 'transcription.result') {
        _handleResult(msg.text, msg.is_final)
      } else if (msg.type === 'transcription.error') {
        status.value = 'error'
        _stopAudio()
      }
    }

    ws.onerror = () => {
      status.value = 'error'
      _stopAudio()
    }

    ws.onclose = () => {
      if (status.value === 'active' || status.value === 'connecting' || status.value === 'checking') {
        status.value = 'idle'
      }
      _stopAudio()
    }
  }

  function stop() {
    if (ws) {
      ws.send(JSON.stringify({ type: 'transcription.stop' }))
      ws.close()
      ws = null
    }
    _stopAudio()
    status.value = 'idle'
  }

  // ── Audio ────────────────────────────────────────────────────────────────

  function _startAudio(stream: MediaStream) {
    try {
      audioCtx = new AudioContext()
      source = audioCtx.createMediaStreamSource(stream)
      // ScriptProcessorNode est deprecated mais fonctionne parfaitement dans Electron.
      // AudioWorklet nécessite un fichier séparé chargeable via URL.
      processor = audioCtx.createScriptProcessor(PROCESSOR_BUFFER, 1, 1)
      const inputRate = audioCtx.sampleRate

      processor.onaudioprocess = (e) => {
        if (status.value !== 'active' || !ws || ws.readyState !== WebSocket.OPEN) return
        const float32 = e.inputBuffer.getChannelData(0)
        const pcm16 = downsampleToInt16(float32, inputRate)
        ws.send(JSON.stringify({ type: 'transcription.audio', data: int16ToBase64(pcm16) }))
      }

      source.connect(processor)
      // Connecté à destination pour que le ScriptProcessor reçoive les données
      // (requis par les navigateurs même si on ne joue pas l'audio ici).
      processor.connect(audioCtx.destination)
    } catch (e) {
      status.value = 'error'
      console.error('[Transcription] audio setup failed', e)
    }
  }

  function _stopAudio() {
    try {
      processor?.disconnect()
      source?.disconnect()
      audioCtx?.close()
    } catch { /* ignore */ }
    processor = null
    source = null
    audioCtx = null
  }

  // ── Résultats ────────────────────────────────────────────────────────────

  function _handleResult(text: string, isFinal: boolean) {
    if (!text) return
    const last = lines.value[lines.value.length - 1]
    if (last && !last.isFinal) {
      // Remplace la ligne intermédiaire en cours
      lines.value[lines.value.length - 1] = { text, isFinal }
    } else {
      lines.value.push({ text, isFinal })
    }
    // Garde au plus 50 lignes finales pour ne pas saturer le DOM
    const finals = lines.value.filter((l) => l.isFinal)
    if (finals.length > 50) {
      const firstFinalIdx = lines.value.findIndex((l) => l.isFinal)
      lines.value.splice(firstFinalIdx, 1)
    }
  }

  return {
    status: readonly(status),
    deniedReason: readonly(deniedReason),
    lines: readonly(lines),
    start,
    stop,
  }
}
